import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  Plus, Trash2, MapPin, AlertTriangle, Package, Lightbulb, Eye, EyeOff, BarChart3, Pencil
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

// Fix leaflet default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const POINT_TYPES = {
  need: { label: 'Need', color: '#EF4444', icon: AlertTriangle, markerColor: '#EF4444' },
  resource: { label: 'Resource', color: '#10B981', icon: Package, markerColor: '#10B981' },
  proposal: { label: 'Proposal', color: '#6366F1', icon: Lightbulb, markerColor: '#6366F1' },
};

const URGENCY_CONFIG = {
  low: { label: 'Low', color: '#6B7280' },
  medium: { label: 'Medium', color: '#F59E0B' },
  high: { label: 'High', color: '#EF4444' },
};

const createColoredIcon = (color, size = 12) => {
  return L.divIcon({
    className: 'custom-map-marker',
    html: `<div style="
      width: ${size * 2}px; height: ${size * 2}px;
      background: ${color};
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 6px rgba(0,0,0,0.35);
      pointer-events: none;
    "></div>`,
    iconSize: [size * 2, size * 2],
    iconAnchor: [size, size],
    popupAnchor: [0, -size],
  });
};

// Click handler to place new points
const MapClickHandler = ({ onClick }) => {
  useMapEvents({ click: (e) => onClick(e.latlng) });
  return null;
};

// Auto-fit bounds
const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length > 0) {
      const bounds = points.map(p => [p.lat, p.lng]);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
    }
  }, [points, map]);
  return null;
};

const NeedsResourcesMap = ({ ideaId, ideaTitle, canManage }) => {
  const [points, setPoints] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visibleLayers, setVisibleLayers] = useState({ need: true, resource: true, proposal: true });
  const [showCreate, setShowCreate] = useState(false);
  const [clickedLatLng, setClickedLatLng] = useState(null);
  const [newPoint, setNewPoint] = useState({
    title: '', description: '', point_type: 'need', category: '', urgency: 'medium',
  });
  const [editingPoint, setEditingPoint] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', point_type: 'need', category: '', urgency: 'medium' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchData(); }, [ideaId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = ideaId ? `?idea_id=${ideaId}` : '';
      const [ptsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/participatory-design/map-points${params}`, { headers }),
        axios.get(`${API}/api/participatory-design/map-points/stats`, { headers }),
      ]);
      setPoints(ptsRes.data || []);
      setStats(statsRes.data);
    } catch { setPoints([]); }
    finally { setLoading(false); }
  };

  const handleMapClick = (latlng) => {
    if (!canManage) return;
    setClickedLatLng(latlng);
    setNewPoint({ title: '', description: '', point_type: 'need', category: '', urgency: 'medium' });
    setShowCreate(true);
  };

  const handleCreate = async () => {
    if (!newPoint.title || !clickedLatLng) { toast.error('Title and location required'); return; }
    try {
      await axios.post(`${API}/api/participatory-design/map-points`, {
        ...newPoint, lat: clickedLatLng.lat, lng: clickedLatLng.lng, idea_id: ideaId || '',
      }, { headers });
      toast.success('Map point added');
      setShowCreate(false);
      fetchData();
    } catch { toast.error('Failed to add point'); }
  };

  const handleDelete = async (pointId) => {
    if (!window.confirm('Delete this map point?\n\nPM/Admin: removes immediately. Social Actor: routed for approval.')) return;
    try {
      const res = await axios.delete(`${API}/api/participatory-design/map-points/${pointId}`, { headers });
      const pending = res.data?.status === 'pending_approval';
      toast.success(pending ? 'Delete request submitted for PM/Admin approval' : 'Point removed');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove'); }
  };

  const openEdit = (pt) => {
    setEditingPoint(pt);
    setEditForm({
      title: pt.title || '',
      description: pt.description || '',
      point_type: pt.point_type || 'need',
      category: pt.category || '',
      urgency: pt.urgency || 'medium',
    });
  };

  const handleSaveEdit = async () => {
    if (!editingPoint) return;
    if (!editForm.title) { toast.error('Title is required'); return; }
    try {
      const res = await axios.patch(
        `${API}/api/participatory-design/map-points/${editingPoint.id}`,
        editForm, { headers },
      );
      const pending = res.data?.status === 'pending_approval';
      toast.success(pending ? 'Changes submitted for PM/Admin approval' : 'Point updated');
      setEditingPoint(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const toggleLayer = (type) => setVisibleLayers(prev => ({ ...prev, [type]: !prev[type] }));

  const filteredPoints = points.filter(p => visibleLayers[p.point_type]);

  const needCount = points.filter(p => p.point_type === 'need').length;
  const resourceCount = points.filter(p => p.point_type === 'resource').length;
  const proposalCount = points.filter(p => p.point_type === 'proposal').length;

  // Default center: Greece
  const defaultCenter = [39.0, 22.0];
  const defaultZoom = 7;

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div></div>;

  return (
    <div className="space-y-4" data-testid="needs-resources-map">
      {/* Stats & Layer Controls */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {Object.entries(POINT_TYPES).map(([type, cfg]) => {
            const count = type === 'need' ? needCount : type === 'resource' ? resourceCount : proposalCount;
            const Icon = cfg.icon;
            return (
              <button
                key={type}
                onClick={() => toggleLayer(type)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${visibleLayers[type] ? 'text-white shadow-sm' : 'bg-white text-gray-500 border-gray-200'}`}
                style={visibleLayers[type] ? { backgroundColor: cfg.color, borderColor: cfg.color } : {}}
                data-testid={`toggle-${type}`}
              >
                {visibleLayers[type] ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                <Icon className="h-3 w-3" />
                {cfg.label} ({count})
              </button>
            );
          })}
        </div>
        {canManage && (
          <p className="text-xs text-gray-500">Click on the map to add a point</p>
        )}
      </div>

      {/* Map */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div
            style={{ height: '480px', position: 'relative', zIndex: 0, isolation: 'isolate' }}
            data-testid="map-container"
            role="region"
            aria-label="Interactive map of needs and resources. Use Tab to navigate markers, Enter to open marker details, arrow keys to pan the map, plus/minus to zoom."
          >
            <MapContainer
              center={defaultCenter}
              zoom={defaultZoom}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
              // Leaflet ships keyboard nav by default; enabling explicitly so
              // future upgrades don't disable it. Tab focuses the map, +/-
              // zoom, arrow keys pan, Tab cycles markers, Enter opens popup.
              keyboard={true}
              keyboardPanDelta={80}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {canManage && <MapClickHandler onClick={handleMapClick} />}
              {filteredPoints.length > 0 && <FitBounds points={filteredPoints} />}
              {filteredPoints.map(p => {
                const cfg = POINT_TYPES[p.point_type] || POINT_TYPES.need;
                const urgCfg = URGENCY_CONFIG[p.urgency] || URGENCY_CONFIG.medium;
                return (
                  <Marker
                    key={p.id}
                    position={[p.lat, p.lng]}
                    icon={createColoredIcon(cfg.markerColor, p.urgency === 'high' ? 14 : 10)}
                    alt={`${cfg.label}: ${p.title}${p.urgency ? ` (${urgCfg.label})` : ''}`}
                    keyboard={true}
                    title={p.title}
                  >
                    <Popup maxWidth={280}>
                      <div className="text-xs">
                        <div className="flex items-center gap-1 mb-1">
                          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cfg.color }}></span>
                          <strong className="text-sm">{p.title}</strong>
                        </div>
                        <div className="flex gap-1 mb-1">
                          <span className="px-1.5 py-0.5 rounded text-white text-xs" style={{ backgroundColor: cfg.color }}>{cfg.label}</span>
                          {p.category && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-xs">{p.category}</span>}
                          <span className="px-1.5 py-0.5 rounded text-xs" style={{ backgroundColor: urgCfg.color + '20', color: urgCfg.color }}>{urgCfg.label}</span>
                        </div>
                        {p.description && <p className="text-gray-600 mt-1">{p.description}</p>}
                        <p className="text-gray-500 mt-1">by {p.created_by} · {p.lat.toFixed(4)}, {p.lng.toFixed(4)}</p>
                        {p.edit_status === 'pending_approval' && (
                          <span className="inline-block mt-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-xs">
                            {p.pending_changes_kind === 'delete' ? 'Pending delete' : 'Pending edit approval'}
                          </span>
                        )}
                        {canManage && (
                          <div className="flex gap-2 mt-1.5">
                            <button onClick={() => openEdit(p)} className="text-blue-600 text-xs hover:underline" data-testid={`edit-point-${p.id}`}>Edit</button>
                            <button
                              onClick={() => handleDelete(p.id)}
                              className="text-red-600 text-xs hover:underline"
                              data-testid={`delete-point-${p.id}`}
                            >Remove point</button>
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
        </CardContent>
      </Card>

      {/* Points List (below map) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {Object.entries(POINT_TYPES).map(([type, cfg]) => {
          const Icon = cfg.icon;
          const typePoints = points.filter(p => p.point_type === type);
          return (
            <Card key={type} className="border" style={{ borderTopColor: cfg.color, borderTopWidth: 3 }} data-testid={`point-list-${type}`}>
              <CardContent className="p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <Icon className="h-4 w-4" style={{ color: cfg.color }} />
                  <span className="text-sm font-semibold" style={{ color: cfg.color }}>{cfg.label}s</span>
                  <Badge variant="outline" className="text-xs">{typePoints.length}</Badge>
                </div>
                {typePoints.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No {cfg.label.toLowerCase()}s mapped</p>
                ) : (
                  <div className="space-y-1 max-h-[200px] overflow-y-auto">
                    {typePoints.map(p => (
                      <div key={p.id} className="flex items-start justify-between p-1.5 rounded bg-gray-50 text-xs">
                        <div>
                          <p className="font-medium text-gray-800">{p.title}</p>
                          <p className="text-gray-500">{p.category} · {URGENCY_CONFIG[p.urgency]?.label}</p>
                        </div>
                        {canManage && (
                          <div className="flex gap-1 shrink-0 ml-1">
                            <button onClick={() => openEdit(p)} className="text-blue-500 hover:text-blue-700" data-testid={`list-edit-${p.id}`}>
                              <Pencil className="h-3 w-3" />
                            </button>
                            <button onClick={() => handleDelete(p.id)} className="text-red-400 hover:text-red-600">
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Create Point Dialog — Leaflet's panes use z-index up to ~700, so we
          force the modal to render above by bumping its z-index well past
          that. Without this the dialog renders behind the map. */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md z-[1300]">
          <DialogHeader><DialogTitle>Add Map Point</DialogTitle></DialogHeader>
          {clickedLatLng && (
            <div className="space-y-3 pt-2">
              <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 p-2 rounded">
                <MapPin className="h-3.5 w-3.5 text-indigo-500" />
                <span>Location: {clickedLatLng.lat.toFixed(5)}, {clickedLatLng.lng.toFixed(5)}</span>
              </div>
              <div><Label>Title *</Label><Input value={newPoint.title} onChange={e => setNewPoint({ ...newPoint, title: e.target.value })} placeholder="e.g., No internet in village X" data-testid="point-title-input" /></div>
              <div><Label>Description</Label><Textarea value={newPoint.description} onChange={e => setNewPoint({ ...newPoint, description: e.target.value })} rows={2} placeholder="Details about this need/resource/proposal..." /></div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label>Type *</Label>
                  <Select value={newPoint.point_type} onValueChange={v => setNewPoint({ ...newPoint, point_type: v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="need">Need</SelectItem>
                      <SelectItem value="resource">Resource</SelectItem>
                      <SelectItem value="proposal">Proposal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Category</Label><Input value={newPoint.category} onChange={e => setNewPoint({ ...newPoint, category: e.target.value })} placeholder="e.g., Infrastructure" className="h-8 text-xs" /></div>
                <div><Label>Urgency</Label>
                  <Select value={newPoint.urgency} onValueChange={v => setNewPoint({ ...newPoint, urgency: v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button size="sm" onClick={handleCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-point-btn">Add Point</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Point Dialog */}
      <Dialog open={!!editingPoint} onOpenChange={(o) => { if (!o) setEditingPoint(null); }}>
        <DialogContent className="max-w-md z-[1300]" data-testid="edit-point-dialog">
          <DialogHeader><DialogTitle>Edit Map Point</DialogTitle></DialogHeader>
          {editingPoint && (
            <div className="space-y-3">
              <div><Label>Title *</Label><Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="h-8 text-xs" data-testid="edit-point-title" /></div>
              <div><Label>Description</Label><Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={2} className="text-xs" /></div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label>Type</Label>
                  <Select value={editForm.point_type} onValueChange={(v) => setEditForm({ ...editForm, point_type: v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="need">Need</SelectItem>
                      <SelectItem value="resource">Resource</SelectItem>
                      <SelectItem value="proposal">Proposal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Category</Label><Input value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} className="h-8 text-xs" /></div>
                <div><Label>Urgency</Label>
                  <Select value={editForm.urgency} onValueChange={(v) => setEditForm({ ...editForm, urgency: v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-gray-500">PM/Admin: changes apply immediately. Project-owner Social Actor: changes route through PM/Admin approval.</p>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setEditingPoint(null)}>Cancel</Button>
                <Button size="sm" onClick={handleSaveEdit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-point-edit-btn">Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NeedsResourcesMap;
